/**
 * NotificationsProvider — app-wide notification state.
 *
 * Owns the realtime WebSocket, the Inbox/Sent unread badge counts, and the
 * "new inbox notification" sound chime (synthesised via Web Audio so no
 * binary asset is needed). Sound is per-user toggleable and remembered in
 * localStorage. Mounted once inside <AuthProvider> so the left-nav badge,
 * the dashboard entry card, and the Notifications page all share one source
 * of truth.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { useAuth } from './auth';
import { NotificationsSocket } from './notificationsWs';
import { toast } from 'sonner';
import SaMailPopup from '@/components/mail/SaMailPopup';

const NotifCtx = createContext(null);

// Genuine-interaction events used to report presence "activity" heartbeats.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];

function soundKey(userId) {
  return `inv_notif_sound_${userId || 'anon'}`;
}

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [inboxUnread, setInboxUnread] = useState(0);
  const [sentUnread, setSentUnread] = useState(0);
  const [priorityUnread, setPriorityUnread] = useState(0);
  const [viewableOnce, setViewableOnce] = useState(0);
  const [wsLive, setWsLive] = useState(false);
  const [soundEnabled, setSoundEnabledState] = useState(true);
  // Wave 4.x — Super-Admin "must-see" mail popups queue (blocking modal).
  const [saPopups, setSaPopups] = useState([]);
  // Bumped whenever a realtime notification arrives so open pages can refetch.
  const [lastEventAt, setLastEventAt] = useState(0);

  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const audioCtxRef = useRef(null);
  const activityCleanupRef = useRef(null);

  // --- Sound preference (per user) -------------------------------------
  useEffect(() => {
    if (!user) return;
    try {
      const v = localStorage.getItem(soundKey(user.id));
      setSoundEnabledState(v === null ? true : v === '1');
    } catch { setSoundEnabledState(true); }
  }, [user?.id]);

  const setSoundEnabled = useCallback((val) => {
    setSoundEnabledState(val);
    try { localStorage.setItem(soundKey(user?.id), val ? '1' : '0'); } catch {}
    if (val) {
      // Play a confirmation chime (also unlocks the AudioContext via gesture).
      playChime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // --- Web Audio chime --------------------------------------------------
  const ensureAudioCtx = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      return audioCtxRef.current;
    } catch { return null; }
  }, []);

  const playChime = useCallback(() => {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      // Two soft sine notes (E6 -> A6) — a calm, premium two-tone chime.
      const notes = [
        { f: 1318.5, t: 0.0 },
        { f: 1760.0, t: 0.12 },
      ];
      notes.forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = f;
        const start = now + t;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.16, start + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.55);
      });
    } catch {}
  }, [ensureAudioCtx]);

  // Unlock the AudioContext on the first user gesture (browser autoplay policy).
  useEffect(() => {
    const unlock = () => { ensureAudioCtx(); };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [ensureAudioCtx]);

  // --- Counts -----------------------------------------------------------
  const refreshCounts = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications/counts');
      setInboxUnread(data.inbox_unread || 0);
      setSentUnread(data.sent_unread || 0);
      setPriorityUnread(data.priority_unread || 0);
      setViewableOnce(data.viewable_once || 0);
    } catch {}
  }, [user?.id]);

  // --- Super-Admin must-see mail popups --------------------------------
  const loadSaPopups = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications/sa-popups');
      setSaPopups(data.items || []);
    } catch { /* keep current queue */ }
  }, [user?.id]);

  const ackSaPopup = useCallback(async (messageId) => {
    try {
      await api.post(`/notifications/sa-popups/${messageId}/ack`);
    } catch { /* still drop locally so the user isn't stuck */ }
    setSaPopups((q) => q.filter((p) => p.message_id !== messageId));
    // Acknowledging marks the mail read — refresh the badge counts.
    refreshCounts();
  }, [refreshCounts]);

  // --- WS + polling lifecycle ------------------------------------------
  useEffect(() => {
    if (!user) {
      setInboxUnread(0); setSentUnread(0); setPriorityUnread(0); setViewableOnce(0);
      setSaPopups([]);
      return;
    }
    refreshCounts();
    // Surface any Super-Admin popups delivered while this user was offline.
    loadSaPopups();
    const token = localStorage.getItem('inv_token');
    if (token && typeof WebSocket !== 'undefined') {
      const s = new NotificationsSocket({
        token,
        onOpen: () => setWsLive(true),
        onClose: () => setWsLive(false),
        onMessage: (msg) => {
          // Wave 3.99ad — Portal-wide access recheck. When a Super Admin
          // flips Maintenance Lockdown / business-hours, the server broadcasts
          // this so every live client re-validates immediately. Probing
          // portal-status lets the axios interceptor hard-logout anyone now
          // denied (Super Admin gets 200 and stays).
          if (msg?.event === 'access_recheck') {
            api.get('/system/portal-status').catch(() => {});
            return;
          }
          // Wave 4.x — An admin ended this session from the Team Presence rail.
          // Wipe the session and bounce to /login with an explanatory banner.
          if (msg?.event === 'force_logout') {
            try {
              sessionStorage.setItem('inv_lockout_reason', 'admin_logout');
              sessionStorage.setItem('inv_lockout_message', `You were signed out by ${msg.data?.by || 'an administrator'}.`);
            } catch { /* noop */ }
            try {
              localStorage.removeItem('inv_token');
              localStorage.removeItem('inv_user');
            } catch { /* noop */ }
            if (window.location.pathname !== '/login') window.location.href = '/login';
            return;
          }
          if (msg?.event === 'notification' && msg.data) {
            setInboxUnread((u) => u + 1);
            setLastEventAt(Date.now());
            // Keep the Priority / Viewable-Once chip counts accurate too.
            refreshCounts();
            const enabled = (() => {
              try { const v = localStorage.getItem(soundKey(user.id)); return v === null ? true : v === '1'; }
              catch { return true; }
            })();
            if (enabled) playChime();
            // Wave 4.x — Mail from a Super Admin is a "must-see": surface the
            // blocking popup (live) instead of a passing toast.
            if (msg.data.sa_popup) {
              loadSaPopups();
            } else {
              toast.message(msg.data.title || 'New notification', {
                description: msg.data.message,
              });
            }
          }
        },
      });
      s.connect();
      socketRef.current = s;

      // Wave 4.x — Presence activity heartbeats. Report genuine interaction
      // (throttled to once / 25s) so the server can mark this user "away" when
      // they've been idle for a while. Independent of the idle-logout guard.
      let lastAct = 0;
      const onActivity = () => {
        const now = Date.now();
        if (now - lastAct > 25000) {
          lastAct = now;
          try { socketRef.current?.sendActivity(); } catch { /* noop */ }
        }
      };
      ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
      activityCleanupRef.current = () => {
        ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      };
    }
    // Poll counts every 60s as a safety net.
    pollRef.current = setInterval(refreshCounts, 60_000);
    return () => {
      if (socketRef.current) { socketRef.current.close(); socketRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (activityCleanupRef.current) { activityCleanupRef.current(); activityCleanupRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = useMemo(() => ({
    inboxUnread, sentUnread, priorityUnread, viewableOnce, wsLive, lastEventAt,
    soundEnabled, setSoundEnabled, playChime,
    refreshCounts,
    setInboxUnread, setSentUnread,
  }), [inboxUnread, sentUnread, priorityUnread, viewableOnce, wsLive, lastEventAt, soundEnabled, setSoundEnabled, playChime, refreshCounts]);

  return (
    <NotifCtx.Provider value={value}>
      {children}
      <SaMailPopup popups={saPopups} onAck={ackSaPopup} />
    </NotifCtx.Provider>
  );
}

export const useNotifications = () => useContext(NotifCtx) || {
  inboxUnread: 0, sentUnread: 0, priorityUnread: 0, viewableOnce: 0, wsLive: false, lastEventAt: 0,
  soundEnabled: true, setSoundEnabled: () => {}, playChime: () => {},
  refreshCounts: () => {}, setInboxUnread: () => {}, setSentUnread: () => {},
};
